package kz.dentalcrm.app.navigation

import androidx.compose.animation.AnimatedContentTransitionScope
import androidx.compose.animation.ContentTransform
import androidx.compose.animation.EnterTransition
import androidx.compose.animation.ExitTransition
import androidx.compose.animation.SizeTransform
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.slideInHorizontally
import androidx.compose.animation.slideOutHorizontally
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusManager
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.platform.LocalSoftwareKeyboardController
import androidx.compose.ui.platform.SoftwareKeyboardController
import androidx.navigation3.runtime.NavKey
import androidx.navigation3.runtime.entryProvider
import androidx.navigation3.runtime.rememberNavBackStack
import androidx.navigation3.scene.Scene
import androidx.navigation3.ui.NavDisplay
import androidx.navigation3.ui.defaultPredictivePopTransitionSpec
import androidx.savedstate.serialization.SavedStateConfiguration
import kotlinx.serialization.modules.SerializersModule
import kotlinx.serialization.modules.polymorphic
import kz.dentalcrm.app.core.network.LogoutEventBus
import kz.dentalcrm.app.core.network.UnauthorizedEventBus
import kz.dentalcrm.app.core.storage.Storage
import kz.dentalcrm.app.domain.AuthRepository
import kz.dentalcrm.app.expectations.usePredictiveBackGesture
import kz.dentalcrm.app.navigation.adaptive.ListDetailScene
import kz.dentalcrm.app.navigation.adaptive.rememberListDetailSceneStrategy
import kz.dentalcrm.app.screens.appointments.add.AddAppointmentScreen
import kz.dentalcrm.app.screens.diagnosis.list.DiagnosisScreen
import kz.dentalcrm.app.screens.employees.add.AddEmployeeScreen
import kz.dentalcrm.app.screens.finance.FinanceScreen
import kz.dentalcrm.app.screens.login.LoginScreen
import kz.dentalcrm.app.screens.main.MainScreen
import kz.dentalcrm.app.screens.patients.detail.PatientDetailScreen
import kz.dentalcrm.app.screens.positions.list.PositionsScreen
import kz.dentalcrm.app.screens.protocols.list.ProtocolsScreen
import kz.dentalcrm.app.screens.services.services.ServicesScreen
import kz.dentalcrm.app.screens.splash.SplashScreen
import kz.dentalcrm.app.screens.treatment.add.AddPatientTreatmentScreen
import kz.dentalcrm.app.screens.initial_inspection.InitialInspectionScreen
import kz.dentalcrm.app.screens.warehouse.WarehouseScreen
import org.koin.compose.koinInject

private val config = SavedStateConfiguration {
    serializersModule = SerializersModule {
        polymorphic(NavKey::class) {
            subclass(SplashRoute::class, SplashRoute.serializer())
            subclass(LoginRoute::class, LoginRoute.serializer())
            subclass(MainRoute::class, MainRoute.serializer())
            subclass(AppointmentsRoute::class, AppointmentsRoute.serializer())
            subclass(EmployeesRoute::class, EmployeesRoute.serializer())
            subclass(PatientsRoute::class, PatientsRoute.serializer())
            subclass(DiagnosisRoute::class, DiagnosisRoute.serializer())
            subclass(ServicesRoute::class, ServicesRoute.serializer())
            subclass(WarehouseRoute::class, WarehouseRoute.serializer())
            subclass(ProtocolsRoute::class, ProtocolsRoute.serializer())
            subclass(FinanceRoute::class, FinanceRoute.serializer())
            subclass(MenuRoute::class, MenuRoute.serializer())
            subclass(PatientDetailRoute::class, PatientDetailRoute.serializer())
            subclass(AddPatientRoute::class, AddPatientRoute.serializer())
            subclass(DiagnosisDetailRoute::class, DiagnosisDetailRoute.serializer())
            subclass(ProtocolDetailRoute::class, ProtocolDetailRoute.serializer())
            subclass(AddAppointmentRoute::class, AddAppointmentRoute.serializer())
            subclass(AddEmployeeRoute::class, AddEmployeeRoute.serializer())
            subclass(EditEmployeeRoute::class, EditEmployeeRoute.serializer())
            subclass(PositionsRoute::class, PositionsRoute.serializer())
            subclass(ServiceCategoriesRoute::class, ServiceCategoriesRoute.serializer())
            subclass(EditAppointmentRoute::class, EditAppointmentRoute.serializer())
            subclass(AddPatientTreatmentRoute::class, AddPatientTreatmentRoute.serializer())
            subclass(InitialInspectionRoute::class, InitialInspectionRoute.serializer())
            subclass(QuickRepliesRoute::class, QuickRepliesRoute.serializer())
            subclass(ReplyByTemplateRoute::class, ReplyByTemplateRoute.serializer())
        }
    }
}

@Composable
fun AppNavDisplay() {
    val navigator: AppNavigator = koinInject()
    val results: NavigationResults = koinInject()
    val storage: Storage = koinInject()
    val authRepository: AuthRepository = koinInject()
    // Always start with SplashRoute - let SplashScreen handle the navigation logic
    val backStack = rememberNavBackStack(
        config,
        if (storage.getAccessToken().isEmpty().not()) {
            MainRoute
        } else {
            LoginRoute
        }
    )

    // Bind navigator to backstack
    LaunchedEffect(navigator) {
        navigator.bind(
            backStack = backStack,
            results = results,
        )
    }

    LaunchedEffect(results) {
//        results.resultFlow<Boolean>(NavResultKeys.SHOULD_FINISH_ACTIVITY)
//            .collectLatest { shouldFinish ->
//                if (shouldFinish) {
//                    results.clear(NavResultKeys.SHOULD_FINISH_ACTIVITY)
//                    onFinish()
//                }
//            }
    }

    // Force logout on 401 Unauthorized
    LaunchedEffect(Unit) {
        UnauthorizedEventBus.events.collect {
            authRepository.clearAllData()
            LogoutEventBus.emit()
        }
    }

    val listDetailStrategy = rememberListDetailSceneStrategy<Any>()

    Box(
        modifier = Modifier.fillMaxSize()
            .clearFocusOnTapOutside(),
    ) {
        NavDisplay(
            modifier = Modifier.fillMaxSize(),
            backStack = backStack,
            onBack = { navigator.navigateUp() },
            sceneStrategy = listDetailStrategy,
            transitionSpec = globalTransitionSpec,
            popTransitionSpec = globalPopTransitionSpec,
            predictivePopTransitionSpec = if (usePredictiveBackGesture) globalPredictivePopTransitionSpec else defaultPredictivePopTransitionSpec(),
            entryProvider = entryProvider<Any> {
                entry<SplashRoute> {
                    SplashScreen()
                }
                entry<LoginRoute> {
                    LoginScreen()
                }
                entry<MainRoute>(
                    metadata = ListDetailScene.listPane()
                ) {
                    MainScreen()
                }
                entry<DiagnosisRoute>(
                    metadata = ListDetailScene.detailPane()
                ) {
                    LimitedContent {
                        DiagnosisScreen()
                    }
                }
                entry<ServicesRoute>(
                    metadata = ListDetailScene.detailPane()
                ) {
                    LimitedContent {
                        ServicesScreen()
                    }
                }
                entry<WarehouseRoute>(
                    metadata = ListDetailScene.detailPane()
                ) {
                    LimitedContent {
                        WarehouseScreen()
                    }
                }
                entry<ProtocolsRoute>(
                    metadata = ListDetailScene.detailPane()
                ) {
                    LimitedContent {
                        ProtocolsScreen()
                    }
                }
                entry<FinanceRoute>(
                    metadata = ListDetailScene.detailPane()
                ) {
                    LimitedContent {
                        FinanceScreen()
                    }
                }
                entry<PatientDetailRoute>(
                    metadata = ListDetailScene.detailPane()
                ) { route ->
                    LimitedContent {
                        PatientDetailScreen(
                            patientId = route.patientId
                        )
                    }
                }
                entry<AddAppointmentRoute>(
                    metadata = ListDetailScene.detailPane()
                ) {
                    LimitedContent {
                        AddAppointmentScreen()
                    }
                }
                entry<AddEmployeeRoute>(
                    metadata = ListDetailScene.detailPane()
                ) {
                    LimitedContent {
                        AddEmployeeScreen()
                    }
                }
                entry<EditEmployeeRoute>(
                    metadata = ListDetailScene.detailPane()
                ) { route ->
                    LimitedContent {
                        kz.dentalcrm.app.screens.employees.edit.EditEmployeeScreen(
                            employeeId = route.employeeId
                        )
                    }
                }
                entry<PositionsRoute>(
                    metadata = ListDetailScene.detailPane()
                ) {
                    LimitedContent {
                        PositionsScreen()
                    }
                }
                entry<ServiceCategoriesRoute>(
                    metadata = ListDetailScene.detailPane()
                ) {
                    LimitedContent {
                        kz.dentalcrm.app.screens.service_categories.list.ServiceCategoriesScreen()
                    }
                }
                entry<ProtocolDetailRoute>(
                    metadata = ListDetailScene.detailPane()
                ) { route ->
                    LimitedContent {
                        kz.dentalcrm.app.screens.protocols.detail.DetailProtocolScreen(
                            protocolId = route.protocolId
                        )
                    }
                }
                entry<EditAppointmentRoute>(
                    metadata = ListDetailScene.detailPane()
                ) { route ->
                    LimitedContent {
                        kz.dentalcrm.app.screens.appointments.edit.EditAppointmentScreen(
                            appointmentId = route.appointmentId
                        )
                    }
                }
                entry<AddPatientTreatmentRoute>(
                    metadata = ListDetailScene.detailPane()
                ) { route ->
                    LimitedContent {
                        AddPatientTreatmentScreen(
                            appointmentId = route.appointmentId,
                            initialTeeth = route.initialTeeth
                        )
                    }
                }
                entry<InitialInspectionRoute>(
                    metadata = ListDetailScene.detailPane()
                ) { route ->
                    LimitedContent {
                        InitialInspectionScreen(
                            appointmentId = route.appointmentId
                        )
                    }
                }
                entry<QuickRepliesRoute>(
                    metadata = ListDetailScene.detailPane()
                ) {
                    LimitedContent {
                        kz.dentalcrm.app.screens.quick_replies.list.QuickRepliesScreen()
                    }
                }
                entry<ReplyByTemplateRoute>(
                    metadata = ListDetailScene.detailPane()
                ) { route ->
                    LimitedContent {
                        kz.dentalcrm.app.screens.reply_by_template.ReplyByTemplateScreen(
                            phone = route.phone
                        )
                    }
                }
            },
            entryDecorators = listOf()
        )
    }
}

fun screenSlideIn(): EnterTransition =
    slideInHorizontally { it }

fun screenSlideOut(): ExitTransition =
    slideOutHorizontally { it }

fun screenFadeIn(): EnterTransition =
    fadeIn()

fun screenFadeOut(): ExitTransition =
    fadeOut()


val globalTransitionSpec: AnimatedContentTransitionScope<Scene<Any>>.() -> ContentTransform = {
    ContentTransform(
        targetContentEnter = screenSlideIn(),
        initialContentExit = screenFadeOut(),
        sizeTransform = SizeTransform(
            clip = true,
            sizeAnimationSpec = { _, _ -> tween(300) }
        )
    )
}

val globalPopTransitionSpec: AnimatedContentTransitionScope<Scene<Any>>.() -> ContentTransform = {
    ContentTransform(
        targetContentEnter = screenFadeIn(),
        initialContentExit = screenSlideOut(),
        sizeTransform = SizeTransform(
            clip = true,
            sizeAnimationSpec = { _, _ -> tween(300) }
        )
    )
}

val globalPredictivePopTransitionSpec: AnimatedContentTransitionScope<Scene<Any>>.(Int) -> ContentTransform =
    { _ ->
        ContentTransform(
            targetContentEnter = screenFadeIn(),
            initialContentExit = screenSlideOut(),
            sizeTransform = SizeTransform(
                clip = false,
                sizeAnimationSpec = { _, _ -> tween(300) }
            )
        )
    }

@OptIn(ExperimentalComposeUiApi::class)
@Composable
fun Modifier.clearFocusOnTapOutside(): Modifier {
    val focusManager: FocusManager = LocalFocusManager.current
    val keyboardController: SoftwareKeyboardController? = LocalSoftwareKeyboardController.current

    return this.pointerInput(focusManager, keyboardController) {
        detectTapGestures(
            onPress = {
                // Use onPress instead of onTap for better iOS compatibility
                // This fires immediately on touch, before gesture recognition
            },
            onTap = {
                keyboardController?.hide()
                focusManager.clearFocus()
            }
        )
    }
}
